/**
 * SandboxSidebar — the 380px right panel of the sandbox detail workspace,
 * mirroring the Flow Designer / Exploratory-Test-Detail sidebar. Underline
 * tabs over a single scrolling content area:
 *   - Overview     → purpose, connect, instance summary, health
 *   - Checklist    → the authoring checklist (only for authoring sandboxes)
 *   - Diagnostics  → provision timing + live host metrics
 */
import React from 'react';
import { Flex, UnderlineTab, UnderlineTabs } from '@chatsdet/ui';
import { Info, ListChecks, Gauge } from '@phosphor-icons/react';
import type { EnvironmentInstance } from '../../../hooks/useEnvironmentInstances';
import { SandboxChecklist } from './SandboxChecklist';
import { SandboxOverviewPanel } from './SandboxOverviewPanel';
import { SandboxDiagnosticsPanel } from './SandboxDiagnosticsPanel';

export type SandboxSidebarTab = 'overview' | 'checklist' | 'diagnostics';

export interface SandboxSidebarProps {
  instance: EnvironmentInstance;
  templateId: string;
  runtimeAvailable: boolean;
  /** Authoring sandboxes own a setup checklist; others don't surface the tab. */
  showChecklist: boolean;
  activeTab: SandboxSidebarTab;
  onTabChange: (tab: SandboxSidebarTab) => void;
}

export function SandboxSidebar({
  instance,
  templateId,
  runtimeAvailable,
  showChecklist,
  activeTab,
  onTabChange,
}: SandboxSidebarProps) {
  return (
    <div className="w-[380px] shrink-0 flex flex-col border-l border-border bg-card">
      <UnderlineTabs
        selectedId={activeTab}
        onSelectionChange={(id) => onTabChange(id as SandboxSidebarTab)}
        layoutId="sandbox-sidebar-tabs"
        className="h-14 shrink-0"
      >
        <UnderlineTab id="overview" className="flex-1 h-full flex items-center justify-center">
          <Flex align="center" gap="xs" justify="center">
            <Info size={16} weight="bold" />
            Overview
          </Flex>
        </UnderlineTab>
        {showChecklist && (
          <UnderlineTab id="checklist" className="flex-1 h-full flex items-center justify-center">
            <Flex align="center" gap="xs" justify="center">
              <ListChecks size={16} weight="bold" />
              Checklist
            </Flex>
          </UnderlineTab>
        )}
        <UnderlineTab id="diagnostics" className="flex-1 h-full flex items-center justify-center">
          <Flex align="center" gap="xs" justify="center">
            <Gauge size={16} weight="bold" />
            Diagnostics
          </Flex>
        </UnderlineTab>
      </UnderlineTabs>

      <div className="flex-1 min-h-0 overflow-hidden">
        {activeTab === 'overview' && <SandboxOverviewPanel instance={instance} />}
        {activeTab === 'checklist' && showChecklist && (
          <div className="h-full overflow-y-auto">
            <SandboxChecklist templateId={templateId} />
          </div>
        )}
        {activeTab === 'diagnostics' && (
          <SandboxDiagnosticsPanel instance={instance} runtimeAvailable={runtimeAvailable} />
        )}
      </div>
    </div>
  );
}

SandboxSidebar.displayName = 'SandboxSidebar';
